/**
 * Painting a planned scene: the building blocks the renderer uses for its
 * cached bitmaps and live layers, plus `paintScene` for one-off static
 * frames (theme previews, reduced motion).
 */
import type { ThemePack } from '@dropstack/core';
import type { SceneSize } from './layout.ts';
import { paintMoving, paintStill, type PaintEnv } from './painters/index.ts';
import { drawParticles } from './particles.ts';
import { particleSeed, planLayers, type PlannedLayer } from './plan.ts';

function envFor(theme: ThemePack, size: SceneSize, pl: PlannedLayer, time: number): PaintEnv {
  return { palette: theme.palette, size, time, seed: pl.seed };
}

/** Paint the never-changing pixels of some layers (moving parts of non-animated layers are baked at time 0). */
export function paintStaticLayers(ctx: CanvasRenderingContext2D, theme: ThemePack, size: SceneSize, layers: readonly PlannedLayer[]): void {
  for (const pl of layers) {
    const env = envFor(theme, size, pl, 0);
    paintStill(ctx, pl.layer, env);
    if (!pl.animated) paintMoving(ctx, pl.layer, env);
  }
}

/** Paint one animated layer's moving part at `time` seconds. */
export function paintLiveLayer(ctx: CanvasRenderingContext2D, theme: ThemePack, size: SceneSize, pl: PlannedLayer, time: number): void {
  paintMoving(ctx, pl.layer, envFor(theme, size, pl, time));
}

/** Paint the theme's ambient particles at `time` seconds. */
export function paintParticles(ctx: CanvasRenderingContext2D, theme: ThemePack, size: SceneSize, time: number): void {
  drawParticles(ctx, theme.particles, size, time, particleSeed(theme.id));
}

export interface StaticFrameOptions {
  /** Scene time in seconds (default 0: every moving thing in its rest pose). */
  time?: number;
  /** Include the ambient particles (default true). */
  particles?: boolean;
}

/**
 * Paint a whole theme straight onto `ctx` (no caching, no parallax), in CSS
 * px: the caller sets up the device-pixel transform.
 */
export function paintScene(ctx: CanvasRenderingContext2D, theme: ThemePack, size: SceneSize, options: StaticFrameOptions = {}): void {
  const time = options.time ?? 0;
  for (const pl of planLayers(theme, false)) {
    const env = envFor(theme, size, pl, time);
    paintStill(ctx, pl.layer, env);
    paintMoving(ctx, pl.layer, env);
  }
  if (options.particles ?? true) paintParticles(ctx, theme, size, time);
}
